import {
	Box,
	Button,
	Checkbox,
	Flex,
	Input,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Text,
	useDisclosure,
} from '@chakra-ui/react'
import { GetIcon, Icons } from '@ds/Icons'
import { DDSTheme, ddsTokens } from '@ds/index'
import { RADII } from '@ds/tokens/legacy/borders'
import { COLORS } from '@ds/tokens/legacy/colors'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Select } from '@/common/components/Select'
import {
	FilterOperation,
	operatorHasValue,
	operatorLabels,
	SqlOperator,
} from '@/common/components/Table2/types'
import { useGetAllFormsQuery } from '@/modules/forms/api'
import { BTN } from '@/styles/components/button'
import { TEXT_ENUM } from '@/styles/components/text'

import {
	FilterCategory,
	FilterState,
	ReportingFilterFieldEnum,
	UTMFiltersProps,
} from '../types'
import { ALL_FILTER_CATEGORIES, isUtmField, VALID_OPERATORS } from '../utils'

interface FormFilterProps extends UTMFiltersProps {
	existingFilters?: FilterState
}

const FormFilter: React.FC<FormFilterProps> = ({
	onFiltersChange,
	existingFilters = {},
}) => {
	const [activeFilters, setActiveFilters] =
		useState<FilterState>(existingFilters)
	const [selectedField, setSelectedField] =
		useState<ReportingFilterFieldEnum | null>(null)
	const [selectedOperator, setSelectedOperator] = useState<SqlOperator>(
		SqlOperator.EQUALS,
	)
	const [inputValue, setInputValue] = useState('')
	const [selectedFormIds, setSelectedFormIds] = useState<string[]>([])
	const { isOpen, onOpen, onClose } = useDisclosure()
	const pendingFilterRef = useRef<{
		field: ReportingFilterFieldEnum
		operator: SqlOperator
		value: string | string[] | null
	} | null>(null)
	const [searchQuery, setSearchQuery] = useState('')
	const [currentCategory, setCurrentCategory] = useState<FilterCategory | null>(
		null,
	)

	const { data: formsFromApi } = useGetAllFormsQuery()

	const tokens = ddsTokens[DDSTheme.light]

	useEffect(() => {
		setActiveFilters(existingFilters)

		if (existingFilters[ReportingFilterFieldEnum.FORM_ID]) {
			const formFilter = existingFilters[ReportingFilterFieldEnum.FORM_ID]
			if (formFilter && Array.isArray(formFilter.value)) {
				setSelectedFormIds(formFilter.value as string[])
			} else {
				setSelectedFormIds([])
			}
		} else {
			setSelectedFormIds([])
		}
	}, [existingFilters])

	const formOptions = useMemo(() => {
		if (!formsFromApi) return []
		return (formsFromApi as { id: string; name: string }[]).map((form) => ({
			value: form.id,
			label: form.name,
		}))
	}, [formsFromApi])

	const filteredFormOptions = useMemo(() => {
		if (!searchQuery.trim()) return formOptions
		return formOptions.filter((option) =>
			option.label.toLowerCase().includes(searchQuery.toLowerCase()),
		)
	}, [formOptions, searchQuery])

	const filterCategories = useMemo(() => {
		const activeFilterKeys = Object.keys(
			activeFilters,
		) as ReportingFilterFieldEnum[]

		return ALL_FILTER_CATEGORIES.filter((category) => {
			if (category.field === ReportingFilterFieldEnum.FORM_ID) {
				return true
			}

			return !activeFilterKeys.includes(
				category.field as ReportingFilterFieldEnum,
			)
		})
	}, [activeFilters])

	const totalFilters = useMemo(() => {
		return Object.keys(activeFilters).length
	}, [activeFilters])

	const setFilter = useCallback(
		(field: ReportingFilterFieldEnum, filter: FilterOperation | undefined) => {
			const updatedFilters = { ...activeFilters }

			if (filter) {
				updatedFilters[field] = filter

				if (
					field === ReportingFilterFieldEnum.FORM_ID &&
					Array.isArray(filter.value)
				) {
					setSelectedFormIds(filter.value as string[])
				}
			} else {
				delete updatedFilters[field]

				if (field === ReportingFilterFieldEnum.FORM_ID) {
					setSelectedFormIds([])
				}
			}

			setActiveFilters(updatedFilters)
			onFiltersChange(updatedFilters)
		},
		[activeFilters, onFiltersChange],
	)

	const handleOperatorChange = (newOperator: SqlOperator) => {
		setSelectedOperator(newOperator)
		if (!operatorHasValue(newOperator) && selectedField) {
			setFilter(selectedField, {
				operator: newOperator,
				value: null,
			})

			setSelectedField(null)
			setSelectedOperator(SqlOperator.EQUALS)
			setInputValue('')
		}
	}

	useEffect(() => {
		if (!isOpen && pendingFilterRef.current) {
			const { field, operator, value } = pendingFilterRef.current
			const updatedFilters = { ...activeFilters }

			if (operator) {
				if (operatorHasValue(operator) && typeof value === 'string' && value) {
					updatedFilters[field] = {
						operator,
						value,
					}
				} else if (
					field === ReportingFilterFieldEnum.FORM_ID &&
					Array.isArray(value) &&
					value.length > 0
				) {
					updatedFilters[field] = {
						operator: SqlOperator.IN,
						value,
					}
				}
			}

			pendingFilterRef.current = null

			setActiveFilters(updatedFilters)
			onFiltersChange(updatedFilters)

			setSelectedField(null)
			setSelectedOperator(SqlOperator.EQUALS)
			setInputValue('')
			setSelectedFormIds([])
		}
	}, [isOpen, activeFilters, onFiltersChange])

	useEffect(() => {
		if (
			selectedField &&
			selectedField !== ReportingFilterFieldEnum.FORM_ID &&
			selectedOperator &&
			operatorHasValue(selectedOperator) &&
			inputValue
		) {
			pendingFilterRef.current = {
				field: selectedField,
				operator: selectedOperator,
				value: inputValue,
			}
		}
	}, [selectedField, selectedOperator, inputValue])

	const applyCurrentFilter = useCallback(() => {
		const updatedFilters = { ...activeFilters }

		if (
			selectedField === ReportingFilterFieldEnum.FORM_ID &&
			selectedFormIds.length > 0
		) {
			updatedFilters[selectedField] = {
				operator: SqlOperator.IN,
				value: selectedFormIds,
			}
		} else if (
			selectedField &&
			selectedField !== ReportingFilterFieldEnum.FORM_ID &&
			selectedOperator &&
			operatorHasValue(selectedOperator) &&
			inputValue
		) {
			updatedFilters[selectedField] = {
				operator: selectedOperator,
				value: inputValue,
			}
		}

		setActiveFilters(updatedFilters)
		onFiltersChange(updatedFilters)

		setSelectedField(null)
		setSelectedOperator(SqlOperator.EQUALS)
		setInputValue('')
	}, [
		selectedField,
		selectedOperator,
		inputValue,
		selectedFormIds,
		activeFilters,
		onFiltersChange,
	])

	const toggleFormSelection = useCallback(
		(formId: string, isSelected: boolean) => {
			const newSelectedFormIds = isSelected
				? [...selectedFormIds, formId]
				: selectedFormIds.filter((id) => id !== formId)

			setSelectedFormIds(newSelectedFormIds)

			const updatedFilters = { ...activeFilters }

			if (newSelectedFormIds.length > 0) {
				updatedFilters[ReportingFilterFieldEnum.FORM_ID] = {
					operator: SqlOperator.IN,
					value: newSelectedFormIds,
				}
			} else {
				delete updatedFilters[ReportingFilterFieldEnum.FORM_ID]
			}

			setActiveFilters(updatedFilters)

			if (JSON.stringify(updatedFilters) !== JSON.stringify(activeFilters)) {
				onFiltersChange(updatedFilters)
			}
		},
		[selectedFormIds, activeFilters, onFiltersChange],
	)

	const renderFilterCategoryList = () => {
		if (currentCategory && currentCategory.children) {
			const availableFilters = currentCategory.children.filter(
				(child) =>
					!Object.keys(activeFilters).includes(
						child.field as ReportingFilterFieldEnum,
					),
			)

			return (
				<Box p={1}>
					<Flex
						alignItems="center"
						mb={3}
						ml={2}
						cursor="pointer"
						onClick={() => setCurrentCategory(null)}
					>
						<GetIcon boxSize={4} icon={Icons.arrow_left} />
						<Text variant={TEXT_ENUM.semibold14} ml={2}>
							{currentCategory.label}
						</Text>
					</Flex>

					<Flex direction="column">
						{availableFilters.length > 0 ? (
							availableFilters.map((category) => (
								<Flex
									key={category.field.toString()}
									px={3}
									py={2.5}
									alignItems="center"
									cursor="pointer"
									_hover={{ bg: COLORS.purple[2] }}
									borderRadius="6px"
									onClick={() => {
										if (isUtmField(category.field)) {
											setSelectedField(category.field)
										}
									}}
									gap={2}
								>
									{category.icon && (
										<GetIcon icon={category.icon} boxSize={4} />
									)}
									<Text flex="1" variant={TEXT_ENUM.medium14}>
										{category.label}
									</Text>
									<GetIcon icon={Icons.chevron_right} boxSize={4} />
								</Flex>
							))
						) : (
							<Box p={3} textAlign="center">
								<Text variant={TEXT_ENUM.medium14} color="gray.500">
									All filters in this category have been applied.
								</Text>
								<Text variant={TEXT_ENUM.regular12} color="gray.400" mt={1}>
									Remove some filters to add new ones.
								</Text>
							</Box>
						)}
					</Flex>
				</Box>
			)
		}

		return (
			<Flex direction="column" p={1} overflowY="auto">
				{filterCategories.map((category) => (
					<Flex
						key={category.field.toString()}
						px={3}
						py={2.5}
						alignItems="center"
						cursor="pointer"
						_hover={{ bg: COLORS.purple[2] }}
						borderRadius="6px"
						onClick={() => {
							if (category.isCategory && category.children) {
								setCurrentCategory(category)
							} else {
								setSelectedField(category.field as ReportingFilterFieldEnum)

								if (
									category.field === ReportingFilterFieldEnum.FORM_ID &&
									activeFilters[ReportingFilterFieldEnum.FORM_ID]
								) {
									const formIdsFilter =
										activeFilters[ReportingFilterFieldEnum.FORM_ID]
									if (formIdsFilter && Array.isArray(formIdsFilter.value)) {
										setSelectedFormIds(formIdsFilter.value as string[])
									}
								}
							}
						}}
						gap={2}
					>
						{category.icon && <GetIcon icon={category.icon} boxSize={4} />}
						<Text flex="1" variant={TEXT_ENUM.medium14}>
							{category.label}
						</Text>
						<GetIcon icon={Icons.chevron_right} boxSize={4} />
					</Flex>
				))}
			</Flex>
		)
	}

	const renderFilterContent = () => {
		if (!selectedField) {
			return renderFilterCategoryList()
		}

		let categoryLabel = selectedField
		let parentCategory: FilterCategory | null = null

		for (const category of ALL_FILTER_CATEGORIES) {
			if (category.field === selectedField) {
				categoryLabel = category.label as ReportingFilterFieldEnum
				break
			}

			if (category.children) {
				const childFilter = category.children.find(
					(child) => child.field === selectedField,
				)
				if (childFilter) {
					categoryLabel = childFilter.label as ReportingFilterFieldEnum
					parentCategory = category
					break
				}
			}
		}

		if (selectedField === ReportingFilterFieldEnum.FORM_ID) {
			return (
				<Box overflowY="visible" mt={3}>
					<Flex
						px={3}
						alignItems="center"
						mb={3}
						cursor="pointer"
						onClick={() => {
							if (parentCategory) {
								setSelectedField(null)
								setCurrentCategory(parentCategory)
							} else {
								setSelectedField(null)
								setCurrentCategory(null)
							}
							setSelectedOperator(SqlOperator.EQUALS)
							setInputValue('')
						}}
					>
						<GetIcon boxSize={4} icon={Icons.arrow_left} />
						<Text variant={TEXT_ENUM.semibold14} ml={2}>
							{categoryLabel}
						</Text>
					</Flex>
					<Box
						position="relative"
						p={2}
						background={tokens.primitive.light.gray[25]}
					>
						<Input
							pl={1}
							variant="unstyled"
							w="full"
							placeholder="Search"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</Box>
					<Box maxH="350px" overflowY="auto" px={1} pt={2}>
						{filteredFormOptions.length > 0 ? (
							filteredFormOptions.map((option) => (
								<Flex
									key={option.value}
									px={2}
									py={2.5}
									alignItems="center"
									_hover={{ bg: COLORS.purple[2] }}
									borderRadius={RADII.md}
									cursor="pointer"
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										toggleFormSelection(
											option.value,
											!selectedFormIds.includes(option.value),
										)
									}}
								>
									<Checkbox
										isChecked={selectedFormIds.includes(option.value)}
										mr={2}
									>
										<Text variant={TEXT_ENUM.medium14}>{option.label}</Text>
									</Checkbox>
								</Flex>
							))
						) : (
							<Flex
								justifyContent="center"
								alignItems="center"
								py={4}
								color={COLORS.gray[7]}
							>
								<Text variant={TEXT_ENUM.regular14}>No forms found</Text>
							</Flex>
						)}
					</Box>
				</Box>
			)
		}

		const operatorOptions = VALID_OPERATORS.map((op) => ({
			label: operatorLabels[op] || op,
			value: op,
		}))

		return (
			<Box p={3} overflowY="visible">
				<Flex
					alignItems="center"
					mb={3}
					mt={2}
					cursor="pointer"
					onClick={() => {
						if (parentCategory) {
							setSelectedField(null)
							setCurrentCategory(parentCategory)
						} else {
							setSelectedField(null)
							setCurrentCategory(null)
						}
						setSelectedOperator(SqlOperator.EQUALS)
						setInputValue('')
					}}
				>
					<GetIcon boxSize={4} icon={Icons.arrow_left} />
					<Text variant={TEXT_ENUM.semibold14} ml={2}>
						{categoryLabel}
					</Text>
				</Flex>

				<Text
					variant={TEXT_ENUM.regular12}
					color={COLORS.gray[9]}
					mb={1}
					ml={0.5}
				>
					Filter Type
				</Text>
				<Flex mb={3}>
					<Box width="100%">
						<Select
							value={{
								label: operatorLabels[selectedOperator],
								value: selectedOperator,
							}}
							onChange={(option: any) => handleOperatorChange(option?.value)}
							options={operatorOptions}
							placeholder="Select operator"
						/>
					</Box>
				</Flex>

				{operatorHasValue(selectedOperator) && (
					<>
						<Text
							variant={TEXT_ENUM.regular12}
							color={COLORS.gray[9]}
							mb={1}
							ml={0.5}
						>
							Value
						</Text>
						<Input
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							placeholder="Enter value..."
							onBlur={() => {
								if (inputValue) {
									applyCurrentFilter()
								}
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && inputValue) {
									applyCurrentFilter()
									onClose()
								}
							}}
						/>
					</>
				)}
			</Box>
		)
	}

	return (
		<Box w="fit-content">
			<Popover
				placement="bottom-start"
				isOpen={isOpen}
				onOpen={onOpen}
				onClose={() => {
					setCurrentCategory(null)
					onClose()
				}}
			>
				<PopoverTrigger>
					<Flex direction="column">
						<Button
							variant={BTN.secondary_v2}
							size="sm"
							h="35px"
							leftIcon={<GetIcon icon={Icons.filter_funnel} boxSize={4} />}
							color={totalFilters > 0 ? COLORS.purple[9] : undefined}
						>
							Filters {totalFilters > 0 ? `(${totalFilters})` : ''}
						</Button>
					</Flex>
				</PopoverTrigger>
				<PopoverContent
					w="320px"
					h="fit-content"
					maxH="calc(100vh - 200px)"
					borderColor={COLORS.background[10]}
					display="flex"
					flexDirection="column"
				>
					<Box flex="1" overflow="visible">
						{renderFilterContent()}
					</Box>
				</PopoverContent>
			</Popover>
		</Box>
	)
}

export default FormFilter
